import React, { useState, useEffect } from 'react';
import useAuthStore from '../stores/useAuthStore';
import useCourseStore from '../stores/useCourseStore';
import DiscussionThread from '../components/DiscussionThread';
import DashboardLayout from '../components/DashboardLayout';
import axiosClient from '../services/api';
import { useToast } from '../components/ToastContainer';

const InstructorDiscussionDashboard = () => {
    const { user } = useAuthStore();
    const toast = useToast();
    const { enrolledCourses: myCourses } = useCourseStore();

    const [threads, setThreads] = useState([]);
    const [newThread, setNewThread] = useState({
        title: '',
        content: '',
        tags: []
    });
    const [newTag, setNewTag] = useState('');
    const [isCreatingThread, setIsCreatingThread] = useState(false);

    const [filters, setFilters] = useState({
        course: 'all',
        status: 'all'
    });
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (user.role === 'instructor') {
            useCourseStore.getState().fetchMyCourses();
        }
    }, [user?.role]);

    useEffect(() => {
        fetchThreads(true);
    }, [filters]);

    const fetchThreads = async (showLoader = true) => {
        if (showLoader) setIsLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.course !== 'all') params.append('course', filters.course);
            if (filters.status !== 'all') params.append('status', filters.status);

            const res = await axiosClient.get(`/discussions/instructor/threads?${params.toString()}`);
            setThreads(res.data.threads);
        } catch (err) {
            toast.error('Failed to load discussions');
        } finally {
            if (showLoader) setIsLoading(false);
        }
    };

    const handleCreateThread = async (e, courseId) => {
        e.preventDefault()
        if (!newThread.title.trim() || !newThread.content.trim()) return;

        try {
            const res = await axiosClient.post(`/discussions/instructor/${courseId}/threads`, newThread);
            setThreads(prev => [res.data.thread, ...prev]);
            setNewThread({ title: '', content: '', tags: [] });
            setNewTag('');
            setIsCreatingThread(false);
            await fetchThreads(false);
            toast.success('Thread created successfully!');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to create thread');
        }
    };

    const handleAddTag = (e) => {
        e.preventDefault();
        if (newTag.trim() && !newThread.tags.includes(newTag.trim())) {
            setNewThread(prev => ({
                ...prev,
                tags: [...prev.tags, newTag.trim()]
            }));
            setNewTag('');
        }
    };

    const handleRemoveTag = (tagToRemove) => {
        setNewThread(prev => ({
            ...prev,
            tags: prev.tags.filter(tag => tag !== tagToRemove)
        }));
    };

    const handleVote = async (targetId, voteType, targetType = 'thread') => {
        try {
            await axiosClient.post('/discussions/vote', { type: voteType, targetId, targetType });
            setTimeout(() => fetchThreads(false), 300);
        } catch (err) {
            toast.error('Failed to update vote');
            fetchThreads(false);
        }
    };

    const handleReply = async (threadId, content) => {
        try {
            const res = await axiosClient.post(`/discussions/threads/${threadId}/replies`, { content });
            setThreads(prev => prev.map(thread =>
                thread._id === threadId
                    ? { ...thread, replies: [res.data.reply, ...(thread.replies || [])] }
                    : thread
            ));
            toast.success('Reply posted!');
        } catch (err) {
            toast.error('Failed to post reply');
        }
    };

    const handleResolveThread = async (threadId, isResolved) => {
        try {
            await axiosClient.put(`/discussions/threads/${threadId}/resolve`, { isResolved });
            fetchThreads(false);
            toast.success(`Thread ${isResolved ? 'resolved' : 'reopened'}!`);
        } catch (err) {
            toast.error('Failed to update thread status');
        }
    };

    return (
        <DashboardLayout userRole="instructor">
            <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-8">
                <div className="max-w-6xl mx-auto px-4">
                    <div className="mb-8">
                        <h1 className="text-3xl font-bold text-neutral-900 dark:text-white">Course Discussions</h1>
                        <p className="text-neutral-600 dark:text-neutral-400 mt-2">
                            Manage discussions across your courses
                        </p>
                    </div>

                    {/* Filters */}
                    <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6 mb-8">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-2 dark:text-neutral-300">Course</label>
                                <select
                                    value={filters.course}
                                    onChange={(e) => setFilters({ ...filters, course: e.target.value })}
                                    className="w-full px-3 py-2 border rounded-lg dark:bg-neutral-700 dark:text-neutral-300 border-neutral-300 dark:border-neutral-600 focus:ring-primary-500"
                                >
                                    <option value="all">Select an approved course</option>
                                    {myCourses
                                        .filter(course => course.status === 'approved')
                                        .map(course => (
                                            <option key={course._id} value={course._id}>
                                                {course.title}
                                            </option>
                                        ))
                                    }
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-2 dark:text-neutral-300">Status</label>
                                <select
                                    value={filters.status}
                                    onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                                    className="w-full px-3 py-2 border rounded-lg dark:bg-neutral-700 dark:text-neutral-300 border-neutral-300 dark:border-neutral-600 focus:ring-primary-500"
                                >
                                    <option value="all">All Threads</option>
                                    <option value="resolved">Resolved</option>
                                    <option value="unresolved">Unresolved</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    {filters.course !== 'all' && (
                        <>
                            <div className="flex justify-between items-start mb-8 flex-wrap">
                                <div>
                                    <p className="text-neutral-600 dark:text-neutral-400 my-2">
                                        Ask questions, share insights, and collaborate with fellow students
                                    </p>
                                </div>
                                <button
                                    onClick={() => setIsCreatingThread(true)}
                                    className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                >
                                    New Thread
                                </button>
                            </div>

                            {/* Create Thread Form */}
                            {isCreatingThread && (
                                <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6 mb-8">
                                    <h2 className="text-xl font-semibold mb-6 dark:text-neutral-300">Create New Thread</h2>
                                    <form onSubmit={handleCreateThread} className="space-y-6">
                                        <div>
                                            <label className="block text-sm font-medium mb-2 dark:text-neutral-400">Title</label>
                                            <input
                                                type="text"
                                                value={newThread.title}
                                                onChange={(e) => setNewThread({ ...newThread, title: e.target.value })}
                                                className="w-full px-3 py-2 border rounded-lg dark:bg-neutral-700 dark:text-neutral-300 border-neutral-300 dark:border-neutral-600 focus:ring-primary-500"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium mb-2 dark:text-neutral-400">Content</label>
                                            <textarea
                                                value={newThread.content}
                                                onChange={(e) => setNewThread({ ...newThread, content: e.target.value })}
                                                rows="6"
                                                className="w-full px-3 py-2 border rounded-lg dark:bg-neutral-700 dark:text-neutral-300 border-neutral-300 dark:border-neutral-600 focus:ring-primary-500"
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium mb-2 dark:text-neutral-400">Tags</label>
                                            <div className="flex flex-wrap gap-2 mb-3">
                                                {newThread.tags.map((tag, index) => (
                                                    <span key={index} className="px-3 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-sm rounded-full flex items-center">
                                                        {tag}
                                                        <button type="button" onClick={() => handleRemoveTag(tag)} className="ml-2">
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                                            </svg>
                                                        </button>
                                                    </span>
                                                ))}
                                            </div>
                                            <div className="flex">
                                                <input
                                                    type="text"
                                                    value={newTag}
                                                    onChange={(e) => setNewTag(e.target.value)}
                                                    onKeyDown={(e) => e.key === 'Enter' && handleAddTag(e)}
                                                    className="flex-1 px-3 py-2 border rounded-lg dark:bg-neutral-700 dark:text-neutral-300 border-neutral-300 dark:border-neutral-600 focus:ring-primary-500"
                                                    placeholder="Add a tag and press Enter"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={handleAddTag}
                                                    className="px-4 py-2 bg-primary-500 text-white rounded-r-lg"
                                                >
                                                    Add
                                                </button>
                                            </div>
                                        </div>
                                        <div className="flex justify-end space-x-3">
                                            <button type="button" onClick={() => setIsCreatingThread(false)} className="px-4 py-2 bg-neutral-200 dark:bg-neutral-700 dark:text-neutral-300 rounded-lg">
                                                Cancel
                                            </button>
                                            <button type="submit" className="px-4 py-2 bg-primary-500 text-white rounded-lg">
                                                Create Thread
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            )}
                        </>
                    )}

                    {/* Threads */}
                    {isLoading ? (
                        <div className="flex justify-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
                        </div>
                    ) : threads.length > 0 ? (
                        <div className="space-y-6">
                            {threads.map(thread => (
                                <>
                                    <DiscussionThread
                                        key={thread._id}
                                        thread={thread}
                                        onVote={handleVote}
                                        onReply={handleReply}
                                        onResolve={handleResolveThread}
                                        isEditable={true} // Pass flag to show resolve button
                                    />
                                </>
                            ))}
                        </div>
                    ) : (
                        <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-8 text-center">
                            <div className="text-6xl mb-4">💬</div>
                            <h3 className="text-xl font-semibold mb-2">No discussions yet</h3>
                            <p className="text-neutral-600 dark:text-neutral-400">
                                Students haven’t posted in your course discussions.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </DashboardLayout>
    );
};

export default InstructorDiscussionDashboard;